import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import Swal from 'sweetalert2';
import DataTable from '../components/DataTable';

const ListaInstructores = () => {
  const [instructores, setInstructores] = useState([]);
  const navigate = useNavigate();

  // Función para obtener los datos
  const getData = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/instructores/listall/');
      const instructoresConFunciones = data.instructores.map((instructor) => ({
        ...instructor,
        imagen: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '50px',
              height: '50px'
            }}>
            {instructor.imgInstructor ? (
              <img
                src={instructor.imgInstructor}
                alt="Instructor"
                className="rounded-circle border border-2 border-dark"
                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
              />
            ) : (
              <i
                className="fa-solid fa-user rounded-circle border border-2 border-dark"
                style={{
                  fontSize: '30px',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              ></i>
            )}
          </div>
        ),
        funciones: (
          <div className="d-flex gap-2">
            {/* 👁️ Botón para abrir modal de firma */}
            <button
              className="btn btn-primary d-flex align-items-center justify-content-center"
              style={{ width: "30px", height: "30px", padding: "0" }}
              data-bs-toggle="modal"
              data-bs-target={`#previewFirma${instructor._id}`}
              title="Ver Firma"
            >
              <i className="fa-solid fa-eye"></i>
            </button>

            {/* Registrar firma */}
            <button
              className="btn btn-primary d-flex align-items-center justify-content-center"
              style={{ width: "30px", height: "30px", padding: "0" }}
              onClick={() => navigate(`/instructor/registrarfirma/${instructor._id}`, { state: { instructor } })}
              title="Registrar Firma"
            >
              <i className="fa-solid fa-signature"></i>
            </button>

            {/* Listar contratos */}
            <button
              className="btn btn-primary"
              style={{ width: "30px", height: "30px", padding: "0" }}
              onClick={() => navigate(`/instructor/listarcontratos/${instructor._id}`)}
              title="Listar Contratos"
            >
              <i className="fa-solid fa-list-check"></i>
            </button>

            {/* Registrar contrato */}
            <button
              className="btn btn-primary d-flex align-items-center justify-content-center"
              style={{ width: "30px", height: "30px", padding: "0" }}
              onClick={() => navigate(`/instructor/registrarcontratos/${instructor._id}`, { state: { instructor } })}
              title="Registrar Contrato"
            >
              <i className="fa-solid fa-file-signature"></i>
            </button>

            {/* Editar instructor */}
            <button
              className="btn btn-warning d-flex align-items-center justify-content-center"
              style={{ width: "30px", height: "30px", padding: "0" }}
              onClick={() => navigate(`/instructor/registrarinstructores/${instructor._id}`, { state: { instructor } })}
              title="Editar Instructor"
            >
              <i className="fa-solid fa-edit"></i>
            </button>

            {/* Eliminar instructor */}
            <button
              className="btn btn-danger d-flex align-items-center justify-content-center"
              style={{ width: "30px", height: "30px", padding: "0" }}
              onClick={() => deleteInstructor(instructor._id)}
              title="Eliminar Instructor"
            >
              <i className="fa-solid fa-trash"></i>
            </button>
          </div>
        ),
        // 👁️ Modal de firma (se inyecta dinámicamente por cada instructor)
        modalFirma: (
          <div
            className="modal fade"
            id={`previewFirma${instructor._id}`}
            tabIndex="-1"
            aria-labelledby="previewFirmaLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="previewFirmaLabel">
                    Firma de {instructor.nombre1Instructor} {instructor.apellido1Instructor}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Cerrar"
                  ></button>
                </div>
                <div className="modal-body text-center">
                  {instructor.firmaInstructor ? (
                    <img
                      src={instructor.firmaInstructor}
                      alt="Firma del Instructor"
                      className="img-fluid border p-2 rounded"
                      style={{ maxHeight: "250px" }}
                    />
                  ) : (
                    <p className="text-muted">Este instructor no tiene firma registrada.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }));
      setInstructores(instructoresConFunciones);
    } catch (error) {
      console.error('Error al cargar los instructores:', error.message);
    }
    // eslint-disable-next-line 
  }, []);

  // Ejecutar getData cuando el componente se monte
  useEffect(() => {
    getData();
  }, [getData]);

  // Función para eliminar un instructor
  const deleteInstructor = async (id) => {
    try {
      Swal.fire({
        title: '¿Está seguro?',
        text: '¡No podrá revertir este proceso!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminar!',
      }).then(async (result) => {
        if (result.isConfirmed) {
          const { data } = await axios.delete(`/api/instructores/${id}`);
          getData(); // Refrescar los datos
          Swal.fire({
            icon: 'success',
            title: data.message,
            showConfirmButton: false,
            timer: 1500,
          });
        }
      });
    } catch (error) {
      console.error('Error al eliminar el instructor:', error.message);
      Swal.fire('Error', 'No se pudo eliminar el instructor.', 'error');
    }
  };

  const columns = React.useMemo(() => [
    { accessor: 'imagen' },
    { Header: 'Documento', accessor: 'documentoInstructor' },
    { Header: 'Nombre', accessor: 'nombre1Instructor' },
    { Header: 'Apellido', accessor: 'apellido1Instructor' },
    { Header: 'Edad', accessor: 'edadInstructor' },
    { Header: 'Sexo', accessor: 'sexoInstructor' },
    { Header: 'Email', accessor: 'emailInstructor' },
    { accessor: 'funciones' },
  ], []);

  return (
    <div className="container-fluid py-1 px-0 shadow-lg rounded-4 bg-light mt-5">
      <div className="py-3">
        <h1>Lista de Instructores</h1>
        {Array.isArray(instructores) && instructores.length > 0 ? (
          <>
            <DataTable columns={columns} data={instructores} />
            {/* Renderizar todos los modales debajo de la tabla */}
            {instructores.map((i) => i.modalFirma)}
          </>
        ) : (
          <p>Cargando instructores...</p>
        )}
      </div>
    </div>
  );
};

export default ListaInstructores;
